import { useEffect, useMemo, useState } from "react";
import {
  ArrowTopRightOnSquareIcon,
  CameraIcon,
  ChatBubbleLeftEllipsisIcon,
  ClipboardDocumentCheckIcon,
  ExclamationTriangleIcon,
  WrenchScrewdriverIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { Link } from "react-router-dom";
import axios from "../../../api/axios";
import { fetchDocuments } from "../../../api/document";
import { fetchChecklist } from "../../../api/quality/checklist";
import { fetchDeviation } from "../../../api/quality/deviation";
import { DirectImageUploadButton } from "../Media/DirectImageUploadButton";
import { DeviationPerformWorkDialog } from "../quality/deviations/DeviationPerformWorkDialog";
import { StatusUpdateSection } from "../statusUpdates/StatusUpdateSection";
import { deviationConsequenceOptions } from "../quality/deviations/utils";
import { PMDialog } from "../../utils/PMDialog";
import { ResourceLoading } from "../../utils/ResourceLoading";
import { checklistItemStatus, deviationStatusLabel } from "./drawingAnnotationUtils";
import {
  observationKindFromModule,
  parseModuleMeta,
  severityColor,
  severityIdFromValue,
  checklistIdFromModule,
  deviationIdFromModule,
  documentIdFromModule,
  existingDrawingPhotoPins,
  resolveChecklistItemContext,
} from "./drawingObservationUtils";
import {
  DRAWING_DOCUMENT_FIELDS,
  DrawingDocumentPinGroups,
  documentImageUrl,
  documentFullImageUrl,
} from "./DrawingDocumentPinGroups";

const DOCUMENT_FIELDS = DRAWING_DOCUMENT_FIELDS;

const DEVIATION_DETAIL_FIELDS =
  "id,number,displayTitle,title,description,consequence,status,severity,priority,place,project,customer,work_order,created_by,created_at,deviation_date,deviation_type,latest_explanation_item,consequence_items";

const DEVIATION_STATUS_STYLES = {
  "1": "bg-[#fef3c7] text-[#92400e] border-[#fcd34d]",
  "2": "bg-[#dbeafe] text-[#1e40af] border-[#93c5fd]",
  "3": "bg-[#dcfce7] text-[#166534] border-[#86efac]",
};

const formatDeviationDate = (value) => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

const CHECKLIST_ITEM_STATUS_STYLES = {
  approved: "bg-[#dcfce7] text-[#166534] border-[#86efac]",
  deviation: "bg-[#fee2e2] text-[#991b1b] border-[#fca5a5]",
  na: "bg-[#f1f5f9] text-[#475569] border-[#cbd5e1]",
  pending: "bg-[#fef3c7] text-[#92400e] border-[#fcd34d]",
  not_approved: "bg-[#fef3c7] text-[#92400e] border-[#fcd34d]",
};

const isImageDocument = (doc) => {
  const type = String(
    doc?.file?.content_type || doc?.content_type || ""
  ).toLowerCase();
  if (type.startsWith("image/")) return true;
  const name = `${doc?.title || ""} ${doc?.file?.url || ""}`;
  return /\.(png|jpe?g|gif|webp|heic|bmp)(\?|#|$)/i.test(name);
};

const imageUrl = documentImageUrl;
// "Open in new tab" must go to the original, not the ~200x200 thumb imageUrl() prefers.
const fullImageUrl = documentFullImageUrl;

const statusDisplayKey = (status) => {
  if (status === "pending") return "not_approved";
  return status || "not_approved";
};

export const DrawingModuleDetailDialog = ({
  t,
  open,
  module,
  drawing,
  checklists = [],
  onClose,
  onToggleSnapshotPrint,
  onFillChecklist,
  onAttachPhoto = null,
  onAttachExistingPhoto = null,
  modules = [],
  onOpenDeviation = null,
  onShowOnDrawing,
  onFocusDocumentPin = null,
  focusedDocumentId = null,
}) => {
  const [loading, setLoading] = useState(false);
  const [photoDocument, setPhotoDocument] = useState(null);
  const [snapshotDocument, setSnapshotDocument] = useState(null);
  const [fetchedItem, setFetchedItem] = useState(null);
  const [fetchedChecklistTitle, setFetchedChecklistTitle] = useState(null);
  const [fetchedDeviation, setFetchedDeviation] = useState(null);
  const [deviationLoading, setDeviationLoading] = useState(false);
  const [deviationDocuments, setDeviationDocuments] = useState([]);
  const [performWorkOpen, setPerformWorkOpen] = useState(false);
  const [deviationTab, setDeviationTab] = useState("details");
  const [projectPhotos, setProjectPhotos] = useState([]);
  const [linkChecklistId, setLinkChecklistId] = useState("");
  const [linkingKey, setLinkingKey] = useState(null);

  const meta = module ? parseModuleMeta(module) : {};
  const kind = module ? observationKindFromModule(module) : null;
  const deviationId = module ? deviationIdFromModule(module) : null;

  const baseChecklistContext = useMemo(
    () => (module ? resolveChecklistItemContext(module, checklists) : null),
    [module, checklists]
  );

  const checklistContext = useMemo(() => {
    if (!baseChecklistContext && !fetchedItem) return null;
    const item = fetchedItem || baseChecklistContext?.item || null;
    const itemTitle =
      item?.displayTitle ||
      item?.title ||
      baseChecklistContext?.itemTitle ||
      null;
    return {
      ...(baseChecklistContext || {}),
      item,
      itemTitle,
      checklistTitle:
        baseChecklistContext?.checklistTitle || fetchedChecklistTitle || null,
    };
  }, [baseChecklistContext, fetchedItem, fetchedChecklistTitle]);

  const checklistId =
    checklistContext?.checklistId ||
    (module ? checklistIdFromModule(module) : null);
  const checklistItemId = checklistContext?.checklistItemId || null;

  const photoChoices = useMemo(() => {
    const snapshotIds = new Set();
    (modules || []).forEach((mod) => {
      const pinMeta = parseModuleMeta(mod);
      if (pinMeta.snapshot_document_id != null) {
        snapshotIds.add(String(pinMeta.snapshot_document_id));
      }
    });
    const fromPins = existingDrawingPhotoPins(modules).map((mod) => {
      const pinMeta = parseModuleMeta(mod);
      return {
        key: `pin-${mod.id}`,
        documentId: pinMeta.document_id,
        title:
          pinMeta.title ||
          pinMeta.caption ||
          t("drawings.observation.photo_default_title"),
        checklistId: checklistIdFromModule(mod),
        photoModule: mod,
      };
    });
    const seen = new Set(fromPins.map((row) => String(row.documentId)));
    const fromProject = (projectPhotos || [])
      .filter((doc) => doc?.id != null && isImageDocument(doc))
      .filter((doc) => !snapshotIds.has(String(doc.id)))
      .filter((doc) => !seen.has(String(doc.id)))
      .filter((doc) => {
        if (doc.drawing_snapshot_document_id != null) {
          snapshotIds.add(String(doc.drawing_snapshot_document_id));
        }
        return true;
      })
      .filter((doc) => !snapshotIds.has(String(doc.id)))
      .map((doc) => ({
        key: `doc-${doc.id}`,
        documentId: doc.id,
        title: doc.title || t("drawings.observation.photo_default_title"),
        checklistId:
          doc.module === "checklist" ? doc.module_param : null,
        photoModule:
          (modules || []).find(
            (mod) => String(documentIdFromModule(mod)) === String(doc.id)
          ) || null,
      }));
    return [...fromPins, ...fromProject];
  }, [modules, projectPhotos, t]);

  useEffect(() => {
    if (!open || !drawing?.project?.id) {
      setProjectPhotos([]);
      return undefined;
    }
    if (kind !== "checklist" && kind !== "photo") return undefined;
    let cancelled = false;
    const loadProjectPhotos = async () => {
      try {
        const response = await axios.get("/documents", {
          params: {
            module: "project",
            module_param: drawing.project.id,
            per_page: 100,
            fields:
              "id,title,file,module,module_param,drawing_id,drawing_snapshot_document_id",
          },
        });
        if (cancelled) return;
        const rows = Array.isArray(response.data?.documents)
          ? response.data.documents
          : [];
        const onThisDrawing = rows.filter(
          (doc) =>
            doc?.drawing_id == null ||
            String(doc.drawing_id) === String(drawing.id)
        );
        setProjectPhotos(onThisDrawing);
      } catch (err) {
        console.error("Error loading project photos for checklist link:", err);
      }
    };
    loadProjectPhotos();
    return () => {
      cancelled = true;
    };
  }, [open, kind, drawing?.id, drawing?.project?.id]);

  useEffect(() => {
    if (!open || !module) {
      setPhotoDocument(null);
      setSnapshotDocument(null);
      setFetchedItem(null);
      setFetchedChecklistTitle(null);
      setFetchedDeviation(null);
      setDeviationTab("details");
      return;
    }

    if (kind !== "photo" || !meta.document_id) {
      setPhotoDocument(null);
      if (kind !== "deviation") setSnapshotDocument(null);
      return;
    }

    let cancelled = false;

    const loadDocuments = async () => {
      setLoading(true);
      try {
        const response = await axios.get(`/documents/${meta.document_id}`, {
          params: { fields: DOCUMENT_FIELDS },
        });
        if (cancelled) return;

        const photo = response.data;
        setPhotoDocument(photo);

        const nestedSnap = photo?.drawing_snapshot_document;
        if (nestedSnap?.file || nestedSnap?.id) {
          if (nestedSnap.file) {
            setSnapshotDocument(nestedSnap);
          } else if (photo?.drawing_snapshot_document_id) {
            const snapResponse = await axios.get(
              `/documents/${photo.drawing_snapshot_document_id}`,
              { params: { fields: DOCUMENT_FIELDS } }
            );
            if (!cancelled) setSnapshotDocument(snapResponse.data);
          }
        } else if (photo?.drawing_snapshot_document_id) {
          const snapResponse = await axios.get(
            `/documents/${photo.drawing_snapshot_document_id}`,
            { params: { fields: DOCUMENT_FIELDS } }
          );
          if (!cancelled) {
            setSnapshotDocument(snapResponse.data);
          }
        } else {
          setSnapshotDocument(null);
        }
      } catch (err) {
        console.error("Error loading observation documents:", err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadDocuments();
    return () => {
      cancelled = true;
    };
  }, [open, module, kind, meta.document_id]);

  // Fetch checklist item title/status when nested payload / related list is incomplete.
  useEffect(() => {
    if (!open || !module || kind !== "photo") {
      setFetchedItem(null);
      setFetchedChecklistTitle(null);
      return;
    }

    const needsFetch =
      checklistId &&
      checklistItemId &&
      (!baseChecklistContext?.itemTitle || !baseChecklistContext?.item);

    if (!needsFetch) {
      setFetchedItem(null);
      setFetchedChecklistTitle(null);
      return;
    }

    let cancelled = false;
    const loadChecklistItem = async () => {
      try {
        const response = await fetchChecklist(checklistId);
        const checklist = response?.data || response;
        if (cancelled || !checklist) return;
        const item = (checklist.items || []).find(
          (row) => String(row.id) === String(checklistItemId)
        );
        if (item) setFetchedItem(item);
        setFetchedChecklistTitle(
          checklist.displayTitle ||
            checklist.title ||
            (checklist.number != null ? `#${checklist.number}` : null)
        );
      } catch (err) {
        console.error("Error loading checklist item for photo pin:", err);
      }
    };

    loadChecklistItem();
    return () => {
      cancelled = true;
    };
  }, [
    open,
    module,
    kind,
    checklistId,
    checklistItemId,
    baseChecklistContext?.itemTitle,
    baseChecklistContext?.item,
  ]);

  const loadDeviationDocuments = async (id) => {
    if (!id) {
      setDeviationDocuments([]);
      return;
    }
    try {
      const response = await fetchDocuments({
        filters: [
          { name: "deviation", value: id },
        ],
        per_page: 50,
        page: 1,
        sortBy: "created_at",
        sort: "DESC",
        fields: DOCUMENT_FIELDS,
      });
      const rows = response?.data || response?.documents || [];
      setDeviationDocuments(Array.isArray(rows) ? rows : []);
    } catch (err) {
      console.error("Error loading deviation documents:", err);
      setDeviationDocuments([]);
    }
  };

  // Load full deviation details + optional sketch/snapshot for the pin dialog.
  useEffect(() => {
    if (!open || !module || kind !== "deviation" || !deviationId) {
      setFetchedDeviation(null);
      setDeviationLoading(false);
      setDeviationDocuments([]);
      setPerformWorkOpen(false);
      if (kind !== "photo") setSnapshotDocument(null);
      return;
    }

    let cancelled = false;

    const loadDeviation = async () => {
      setDeviationLoading(true);
      try {
        const response = await fetchDeviation(deviationId, {
          fields: DEVIATION_DETAIL_FIELDS,
        });
        if (cancelled) return;
        const data = response?.data?.id ? response.data : response?.data || response;
        setFetchedDeviation(data?.id ? data : null);
      } catch (err) {
        console.error("Error loading deviation for drawing pin:", err);
        if (!cancelled) setFetchedDeviation(null);
      } finally {
        if (!cancelled) setDeviationLoading(false);
      }
    };

    const loadSnapshot = async () => {
      const snapshotId = meta.snapshot_document_id;
      if (!snapshotId) {
        setSnapshotDocument(null);
        return;
      }
      try {
        const snapResponse = await axios.get(`/documents/${snapshotId}`, {
          params: { fields: DOCUMENT_FIELDS },
        });
        if (!cancelled) setSnapshotDocument(snapResponse.data);
      } catch (err) {
        console.error("Error loading deviation snapshot:", err);
        if (!cancelled) setSnapshotDocument(null);
      }
    };

    loadDeviation();
    loadSnapshot();
    loadDeviationDocuments(deviationId);
    return () => {
      cancelled = true;
    };
  }, [open, module, kind, deviationId, meta.snapshot_document_id]);

  const deviationForPreview =
    fetchedDeviation || module?.deviation || null;

  if (!module) return null;

  const itemStatus = checklistContext?.item
    ? checklistItemStatus(checklistContext.item)
    : null;
  const itemStatusKey = itemStatus ? statusDisplayKey(itemStatus) : null;

  const deviation =
    fetchedDeviation ||
    module.deviation ||
    null;

  const title =
    (kind === "photo" && checklistContext?.itemTitle) ||
    (kind === "photo" && checklistContext?.checklistTitle) ||
    meta.title ||
    deviation?.displayTitle ||
    deviation?.title ||
    module.deviation?.displayTitle ||
    module.deviation?.title ||
    t(`drawings.module.${module.module_type}`);

  const deviationSeverityId =
    meta.severity || severityIdFromValue(deviation?.severity);
  const explanationItem = deviation?.latest_explanation_item;
  const consequenceItems = Array.isArray(deviation?.consequence_items)
    ? deviation.consequence_items
    : [];
  const primaryConsequenceItem = consequenceItems[0] || null;
  const deviationDescription =
    explanationItem?.description ||
    deviation?.description ||
    meta.description ||
    null;
  const deviationConsequence =
    primaryConsequenceItem?.description ||
    deviation?.consequence ||
    meta.consequence ||
    null;
  const consequenceTypeLabel = (() => {
    const typeId =
      primaryConsequenceItem?.consequence || meta.consequence_type || null;
    if (typeId == null || typeId === "") return null;
    const opt = deviationConsequenceOptions.find(
      (item) => String(item.value) === String(typeId)
    );
    return opt
      ? t(`drawings.observation.consequence_type.${opt.label}`, {
          defaultValue: opt.title,
        })
      : null;
  })();
  const deviationWorkOrder = deviation?.work_order || null;
  const deviationProjectLabel =
    deviation?.project?.displayTitle ||
    deviation?.project?.project_number ||
    (drawing?.project?.displayTitle || drawing?.project?.name) ||
    null;
  const deviationPlace = deviation?.place || meta.place || null;
  const deviationCreatedBy =
    deviation?.created_by?.displayTitle ||
    deviation?.created_by?.name ||
    null;
  const deviationCreatedAt = formatDeviationDate(
    deviation?.deviation_date || deviation?.created_at
  );
  const deviationTypeLabel =
    deviation?.deviation_type?.displayTitle ||
    deviation?.deviation_type?.title ||
    meta.category ||
    null;
  const deviationNumber = deviation?.number || module.deviation?.number;
  const openDeviationId = deviation?.id || deviationId;

  const deviationUploadPreviewOptions = {
    drawingSupport: true,
    project: drawing?.project || deviationForPreview?.project || undefined,
    defaultDrawing: drawing || undefined,
    lockDrawing: Boolean(drawing?.id),
    autoOpenPositionPicker: Boolean(drawing?.id),
    enableSketch: true,
    fullCanvasPlacement: true,
    // Soft-close detail dialog while placing pin; resume after place/cancel.
    suspendModuleDetailId: module?.id ?? null,
    suspendDeviationId: openDeviationId ?? null,
  };

  const TypeIcon =
    kind === "photo"
      ? CameraIcon
      : kind === "note"
        ? ChatBubbleLeftEllipsisIcon
        : kind === "checklist"
          ? ClipboardDocumentCheckIcon
          : ExclamationTriangleIcon;

  const linkExistingPhoto = async (choice, checklistTargetId) => {
    if (!onAttachExistingPhoto || checklistTargetId == null || !choice?.documentId) {
      return;
    }
    setLinkingKey(choice.key);
    try {
      await onAttachExistingPhoto({
        documentId: choice.documentId,
        photoModule: choice.photoModule,
        checklistId: checklistTargetId,
      });
    } finally {
      setLinkingKey(null);
    }
  };

  const pageLabel =
    typeof meta.page === "number"
      ? `${t("drawings.observation.page")} ${meta.page + 1}`
      : null;

  const resolvedChecklistId = checklistId || checklistContext?.checklist?.id;

  return (
    <>
    <PMDialog
      open={open}
      onClose={onClose}
      size="lg"
      zIndex={130}
      panelClassName="!p-0 sm:!p-0"
    >
      <div className="flex h-full max-h-[100dvh] flex-col overflow-hidden sm:max-h-[min(92dvh,720px)]">
        <div className="flex flex-shrink-0 items-center justify-between border-b border-[#e5e8ec] px-3 py-2 sm:px-4 sm:py-3.5">
          <div className="flex min-w-0 items-center gap-2 sm:gap-2.5">
            <span
              className="grid h-8 w-8 flex-none place-items-center rounded-lg text-white"
              style={{ background: module.color || "#2b6ef0" }}
            >
              <TypeIcon className="h-4 w-4" />
            </span>
            <div className="min-w-0">
              <h2 className="truncate text-[15px] font-semibold text-[#14171c]">{title}</h2>
              <p className="text-[11px] text-[#59616d]">
                {t(`drawings.observation.type.${kind || "note"}`)}
                {pageLabel ? ` · ${pageLabel}` : ""}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="grid h-9 w-9 place-items-center rounded-md text-[#59616d] hover:bg-[#eef0f3] sm:h-8 sm:w-8"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-3 py-2.5 sm:px-4 sm:py-4">
          {loading && kind === "photo" ? (
            <ResourceLoading size="sm" />
          ) : (
            <>
              {/* Checklist context for photo pins attached to a checklist item */}
              {kind === "photo" && resolvedChecklistId && (
                <div className="mb-4 rounded-[10px] border border-[#bfdcf7] bg-[#eaf3fd] p-3">
                  <p className="text-[10px] font-semibold uppercase tracking-[0.04em] text-[#0b3a78]">
                    {checklistContext?.itemTitle
                      ? t("drawings.viewer.checklist_pin_item_label")
                      : t("drawings.viewer.checklist_pin_checklist_label")}
                  </p>
                  <div className="mt-1 flex flex-wrap items-center gap-2">
                    <p className="min-w-0 text-[14px] font-semibold text-[#14171c]">
                      {checklistContext?.itemTitle ||
                        checklistContext?.checklistTitle ||
                        meta.checklist_title ||
                        t("drawings.viewer.checklist_pin_checklist_label")}
                    </p>
                    {itemStatusKey ? (
                      <span
                        className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-semibold ${
                          CHECKLIST_ITEM_STATUS_STYLES[itemStatusKey] ||
                          CHECKLIST_ITEM_STATUS_STYLES.not_approved
                        }`}
                      >
                        {t(`drawings.viewer.checklist_status.${itemStatusKey}`)}
                      </span>
                    ) : null}
                  </div>
                  {checklistContext?.itemTitle && checklistContext?.checklistTitle ? (
                    <p className="mt-0.5 text-[12px] text-[#59616d]">
                      {t("drawings.viewer.checklist_pin_checklist_label")}:{" "}
                      {checklistContext.checklistTitle}
                    </p>
                  ) : null}
                  <div className="mt-3 flex flex-wrap items-center gap-2">
                    <button
                      type="button"
                      onClick={() => onFillChecklist?.(resolvedChecklistId)}
                      className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-[9px] border border-[#2b6ef0] bg-[#2b6ef0] px-3 py-2.5 text-[13px] font-semibold text-white hover:bg-[#1d4fb0]"
                    >
                      <ClipboardDocumentCheckIcon className="h-4 w-4" />
                      {t("drawings.viewer.checklist_fill")}
                    </button>
                    {onShowOnDrawing ? (
                      <button
                        type="button"
                        onClick={() => onShowOnDrawing(module)}
                        className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-[9px] border border-[#bfdcf7] bg-white px-3 py-2.5 text-[13px] font-semibold text-[#0b3a78] hover:bg-[#eaf3fd]"
                      >
                        {t("drawings.viewer.show_pin_on_drawing")}
                      </button>
                    ) : null}
                    <Link
                      to={`/dashboard/hse-ks/checklists/${resolvedChecklistId}`}
                      className="grid h-10 w-10 place-items-center rounded-[9px] border border-[#e5e8ec] bg-white text-[#2b6ef0] hover:bg-[#eaf1fe]"
                      title={t("drawings.viewer.open_checklist")}
                      aria-label={t("drawings.viewer.open_checklist")}
                    >
                      <ArrowTopRightOnSquareIcon className="h-5 w-5" />
                    </Link>
                  </div>
                </div>
              )}

              {/* Deviation context for photo pins attached to an avvik */}
              {kind === "photo" && deviationId && (
                <div className="mb-4 rounded-[10px] border border-[#f5c2c4] bg-[#fff5f5] p-3">
                  <p className="text-[10px] font-semibold uppercase tracking-[0.04em] text-[#991b1b]">
                    {t("drawings.viewer.deviation_pin_label")}
                  </p>
                  <div className="mt-1 flex flex-wrap items-center gap-2">
                    {(module.deviation?.number ?? meta.deviation_number) != null && (
                      <span className="rounded-md bg-[#14171c] px-2 py-0.5 font-mono text-[12px] font-semibold text-white">
                        #{module.deviation?.number ?? meta.deviation_number}
                      </span>
                    )}
                    <p className="min-w-0 text-[14px] font-semibold text-[#14171c]">
                      {module.deviation?.displayTitle ||
                        module.deviation?.title ||
                        meta.deviation_title ||
                        t("drawings.viewer.deviation_pin_label")}
                    </p>
                    {module.deviation?.status != null && (
                      <span
                        className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-semibold ${
                          DEVIATION_STATUS_STYLES[String(module.deviation.status)] ||
                          "bg-[#f1f5f9] text-[#475569] border-[#cbd5e1]"
                        }`}
                      >
                        {deviationStatusLabel(module.deviation.status, t)}
                      </span>
                    )}
                  </div>
                  <div className="mt-3 flex flex-wrap items-center gap-2">
                    <button
                      type="button"
                      onClick={() => onOpenDeviation?.(deviationId, module)}
                      className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-[9px] border border-[#e5484d] bg-[#e5484d] px-3 py-2.5 text-[13px] font-semibold text-white hover:bg-[#c93a3f]"
                    >
                      <ExclamationTriangleIcon className="h-4 w-4" />
                      {t("drawings.observation.open_deviation")}
                    </button>
                    {onShowOnDrawing ? (
                      <button
                        type="button"
                        onClick={() => onShowOnDrawing(module)}
                        className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-[9px] border border-[#f5c2c4] bg-white px-3 py-2.5 text-[13px] font-semibold text-[#991b1b] hover:bg-[#fff5f5]"
                      >
                        {t("drawings.viewer.show_pin_on_drawing")}
                      </button>
                    ) : null}
                    <Link
                      to={`/dashboard/hse-ks/deviations/${deviationId}`}
                      className="grid h-10 w-10 place-items-center rounded-[9px] border border-[#e5e8ec] bg-white text-[#e5484d] hover:bg-[#fff5f5]"
                      title={t("drawings.observation.go_to_deviation")}
                      aria-label={t("drawings.observation.go_to_deviation")}
                    >
                      <ArrowTopRightOnSquareIcon className="h-5 w-5" />
                    </Link>
                  </div>
                </div>
              )}

              {kind === "photo" && (
                <div className="space-y-4">
                  {(photoDocument || snapshotDocument) && (
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                      {photoDocument && (
                        <div className="overflow-hidden rounded-[10px] border border-[#e5e8ec] bg-white">
                          <h3 className="border-b border-[#e5e8ec] bg-[#f8fafc] px-2.5 py-1.5 text-[10px] font-semibold uppercase tracking-[0.045em] text-[#59616d]">
                            {t("drawings.observation.field.photo")}
                          </h3>
                          {imageUrl(photoDocument) ? (
                            <a
                              href={fullImageUrl(photoDocument)}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="block bg-[#f6f7f9]"
                            >
                              <img
                                src={imageUrl(photoDocument)}
                                alt={photoDocument.title || title}
                                className="max-h-[240px] w-full object-contain"
                              />
                            </a>
                          ) : (
                            <p className="p-3 text-sm text-[#59616d]">
                              {photoDocument.title}
                            </p>
                          )}
                        </div>
                      )}

                      {snapshotDocument && (
                        <div className="overflow-hidden rounded-[10px] border border-[#e5e8ec] bg-white">
                          <h3 className="border-b border-[#e5e8ec] bg-[#f8fafc] px-2.5 py-1.5 text-[10px] font-semibold uppercase tracking-[0.045em] text-[#59616d]">
                            {t("drawings.observation.snapshot_title")}
                          </h3>
                          {imageUrl(snapshotDocument) ? (
                            <a
                              href={fullImageUrl(snapshotDocument)}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="block bg-[#f6f7f9]"
                            >
                              <img
                                src={imageUrl(snapshotDocument)}
                                alt={snapshotDocument.title}
                                className="max-h-[240px] w-full object-contain"
                              />
                            </a>
                          ) : null}
                        </div>
                      )}
                    </div>
                  )}

                  {photoDocument && (
                    <label className="flex cursor-pointer items-start gap-2 text-[13px] text-[#14171c]">
                      <input
                        type="checkbox"
                        className="mt-0.5"
                        checked={
                          photoDocument?.include_drawing_snapshot_in_print !== false
                        }
                        onChange={(event) =>
                          onToggleSnapshotPrint?.(
                            photoDocument.id,
                            event.target.checked
                          )
                        }
                      />
                      <span>{t("drawings.observation.include_snapshot_in_print")}</span>
                    </label>
                  )}

                  {meta.caption && (
                    <p className="text-[13px] text-[#14171c]">{meta.caption}</p>
                  )}

                  {!resolvedChecklistId && (checklists || []).length > 0 && meta.document_id && (
                    <div className="rounded-[10px] border border-[#e5e8ec] bg-[#f6f7f9] p-3">
                      <p className="mb-2 text-[11px] font-semibold uppercase tracking-[0.045em] text-[#59616d]">
                        {t("drawings.observation.field.link_checklist")}
                      </p>
                      <div className="flex flex-col gap-2 sm:flex-row">
                        <select
                          className="h-10 min-w-0 flex-1 rounded-[9px] border border-[#d6dae0] bg-white px-2 text-[13px] text-[#14171c]"
                          value={linkChecklistId}
                          onChange={(event) => setLinkChecklistId(event.target.value)}
                        >
                          <option value="">
                            {t("drawings.observation.checklist_none")}
                          </option>
                          {(checklists || []).map((item) => (
                            <option key={item.id} value={String(item.id)}>
                              {item.displayTitle || item.title || `#${item.id}`}
                            </option>
                          ))}
                        </select>
                        <button
                          type="button"
                          disabled={!linkChecklistId || linkingKey === "this-photo"}
                          onClick={() =>
                            linkExistingPhoto(
                              {
                                key: "this-photo",
                                documentId: meta.document_id,
                                photoModule: module,
                              },
                              linkChecklistId
                            )
                          }
                          className="inline-flex items-center justify-center rounded-[9px] border border-[#2b6ef0] bg-white px-3 py-2 text-[13px] font-semibold text-[#0b3a78] hover:bg-[#eaf3fd] disabled:opacity-50"
                        >
                          {t("drawings.viewer.checklist_attach_existing")}
                        </button>
                      </div>
                    </div>
                  )}

                  {!photoDocument && !loading && (
                    <p className="text-sm text-[#59616d]">
                      {t("drawings.observation.document_missing")}
                    </p>
                  )}
                </div>
              )}

              {kind === "note" && (
                <div>
                  <h3 className="mb-2 text-[11px] font-semibold uppercase tracking-[0.045em] text-[#59616d]">
                    {t("drawings.observation.field.note")}
                  </h3>
                  <p className="whitespace-pre-wrap rounded-[10px] border border-[#e5e8ec] bg-[#f6f7f9] p-3 text-[14px] leading-relaxed text-[#14171c]">
                    {meta.title || t("drawings.observation.note_empty")}
                  </p>
                </div>
              )}

              {kind === "checklist" && (
                <div className="space-y-3">
                  {(() => {
                    if (!resolvedChecklistId) return null;
                    const availablePhotos = photoChoices.filter(
                      (choice) =>
                        String(choice.checklistId || "") !==
                        String(resolvedChecklistId)
                    );
                    return (
                      <>
                      <div className="flex flex-wrap items-center gap-2">
                        <button
                          type="button"
                          onClick={() => onFillChecklist?.(resolvedChecklistId)}
                          className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-[9px] border border-[#2b6ef0] bg-[#2b6ef0] px-3 py-2.5 text-[13px] font-semibold text-white hover:bg-[#1d4fb0]"
                        >
                          <ClipboardDocumentCheckIcon className="h-4 w-4" />
                          {t("drawings.viewer.checklist_fill")}
                        </button>
                        {onAttachPhoto ? (
                          <button
                            type="button"
                            onClick={() => onAttachPhoto(resolvedChecklistId)}
                            className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-[9px] border border-[#bfdcf7] bg-white px-3 py-2.5 text-[13px] font-semibold text-[#0b3a78] hover:bg-[#eaf3fd]"
                          >
                            <CameraIcon className="h-4 w-4" />
                            {t("drawings.viewer.checklist_upload_new_photo")}
                          </button>
                        ) : null}
                        <Link
                          to={`/dashboard/hse-ks/checklists/${resolvedChecklistId}`}
                          className="grid h-10 w-10 place-items-center rounded-[9px] border border-[#e5e8ec] bg-white text-[#2b6ef0] hover:bg-[#eaf1fe]"
                          title={t("drawings.viewer.open_checklist")}
                          aria-label={t("drawings.viewer.open_checklist")}
                        >
                          <ArrowTopRightOnSquareIcon className="h-5 w-5" />
                        </Link>
                      </div>
                      <div className="rounded-[10px] border border-[#e5e8ec] bg-[#f6f7f9] p-3">
                        <p className="mb-2 text-[11px] font-semibold uppercase tracking-[0.045em] text-[#59616d]">
                          {t("drawings.viewer.checklist_existing_photos")}
                        </p>
                        {availablePhotos.length === 0 ? (
                          <p className="text-[12px] text-[#59616d]">
                            {t("drawings.viewer.checklist_existing_photos_empty")}
                          </p>
                        ) : (
                          <ul className="space-y-1.5">
                            {availablePhotos.map((choice) => (
                              <li
                                key={choice.key}
                                className="flex items-center gap-2 rounded-[8px] bg-white px-2 py-1.5"
                              >
                                <span className="min-w-0 flex-1 truncate text-[13px] text-[#14171c]">
                                  {choice.title}
                                </span>
                                <button
                                  type="button"
                                  disabled={
                                    !onAttachExistingPhoto ||
                                    linkingKey === choice.key
                                  }
                                  onClick={() =>
                                    linkExistingPhoto(choice, resolvedChecklistId)
                                  }
                                  className="shrink-0 rounded-[7px] border border-[#bfdcf7] px-2 py-1 text-[12px] font-semibold text-[#0b3a78] hover:bg-[#eaf3fd] disabled:opacity-50"
                                >
                                  {t("drawings.viewer.checklist_attach_existing")}
                                </button>
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                      </>
                    );
                  })()}
                </div>
              )}

              {kind === "deviation" && (
                <div className="space-y-4">
                  {deviationLoading && !deviation ? (
                    <ResourceLoading size="sm" />
                  ) : (
                    <>
                      <div className="flex flex-wrap items-center gap-2">
                        {deviationNumber != null && (
                          <span className="rounded-md bg-[#14171c] px-2 py-0.5 font-mono text-[12px] font-semibold text-white">
                            #{deviationNumber}
                          </span>
                        )}
                        {deviation?.status != null && (
                          <span
                            className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-semibold ${
                              DEVIATION_STATUS_STYLES[String(deviation.status)] ||
                              "bg-[#f1f5f9] text-[#475569] border-[#cbd5e1]"
                            }`}
                          >
                            {deviationStatusLabel(deviation.status, t)}
                          </span>
                        )}
                        {deviationSeverityId && (
                          <span
                            className="inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold text-white"
                            style={{ background: severityColor(deviationSeverityId) }}
                          >
                            {t(`drawings.observation.severity.${deviationSeverityId}`)}
                          </span>
                        )}
                        {openDeviationId && (
                          <Link
                            to={`/dashboard/hse-ks/deviations/${openDeviationId}`}
                            className="ml-auto inline-flex items-center gap-1 rounded-[7px] border border-[#e5e8ec] bg-white px-2 py-1 text-[11px] font-semibold text-[#2b6ef0] hover:bg-[#eaf1fe]"
                            title={t("drawings.observation.open_deviation_page")}
                          >
                            <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5" />
                            {t("drawings.observation.open_deviation_page")}
                          </Link>
                        )}
                      </div>

                      <div className="flex gap-1 rounded-[10px] border border-[#e5e8ec] bg-[#f6f7f9] p-1">
                        {[
                          {
                            id: "details",
                            label: t("drawings.observation.tab.details"),
                          },
                          {
                            id: "comments",
                            label: t("drawings.observation.tab.comments"),
                          },
                        ].map((tab) => (
                          <button
                            key={tab.id}
                            type="button"
                            onClick={() => setDeviationTab(tab.id)}
                            className={`flex-1 rounded-[8px] px-3 py-2 text-[12.5px] font-semibold transition-colors ${
                              deviationTab === tab.id
                                ? "bg-white text-[#1d4fb0] shadow-sm"
                                : "text-[#59616d] hover:text-[#14171c]"
                            }`}
                          >
                            {tab.label}
                          </button>
                        ))}
                      </div>

                      {deviationTab === "comments" && openDeviationId ? (
                        <StatusUpdateSection
                          subjectType="deviation"
                          subjectId={openDeviationId}
                          projectId={
                            deviation?.project?.id || drawing?.project?.id || null
                          }
                          compact
                          title={t("drawings.observation.tab.comments")}
                        />
                      ) : null}

                      {deviationTab === "details" && (
                      <>
                      {(title || meta.title) && (
                        <div>
                          <h3 className="mb-1 text-[11px] font-semibold uppercase tracking-[0.045em] text-[#59616d]">
                            {t("drawings.observation.field.title")}
                          </h3>
                          <p className="text-[14px] font-semibold text-[#14171c]">
                            {title || meta.title}
                          </p>
                        </div>
                      )}

                      {deviationDescription && (
                        <div>
                          <h3 className="mb-1 text-[11px] font-semibold uppercase tracking-[0.045em] text-[#59616d]">
                            {t("drawings.observation.field.explanation")}
                          </h3>
                          <p className="whitespace-pre-wrap rounded-[10px] border border-[#e5e8ec] bg-[#f6f7f9] p-3 text-[13px] leading-relaxed text-[#14171c]">
                            {deviationDescription}
                          </p>
                        </div>
                      )}

                      {consequenceTypeLabel && (
                        <div>
                          <h3 className="mb-1 text-[11px] font-semibold uppercase tracking-[0.045em] text-[#59616d]">
                            {t("drawings.observation.field.consequence_type")}
                          </h3>
                          <p className="text-[13px] text-[#14171c]">
                            {consequenceTypeLabel}
                          </p>
                        </div>
                      )}

                      {deviationConsequence && (
                        <div>
                          <h3 className="mb-1 text-[11px] font-semibold uppercase tracking-[0.045em] text-[#59616d]">
                            {t("drawings.observation.field.consequence")}
                          </h3>
                          <p className="whitespace-pre-wrap rounded-[10px] border border-[#e5e8ec] bg-[#f6f7f9] p-3 text-[13px] leading-relaxed text-[#14171c]">
                            {deviationConsequence}
                          </p>
                        </div>
                      )}

                      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                        {deviationTypeLabel && (
                          <div>
                            <h3 className="mb-1 text-[11px] font-semibold uppercase tracking-[0.045em] text-[#59616d]">
                              {t("drawings.observation.field.category")}
                            </h3>
                            <p className="text-[13px] text-[#14171c]">{deviationTypeLabel}</p>
                          </div>
                        )}
                        {deviationProjectLabel && (
                          <div>
                            <h3 className="mb-1 text-[11px] font-semibold uppercase tracking-[0.045em] text-[#59616d]">
                              {t("drawings.observation.field.project")}
                            </h3>
                            <p className="text-[13px] text-[#14171c]">{deviationProjectLabel}</p>
                          </div>
                        )}
                        {deviationPlace && (
                          <div>
                            <h3 className="mb-1 text-[11px] font-semibold uppercase tracking-[0.045em] text-[#59616d]">
                              {t("drawings.observation.field.place")}
                            </h3>
                            <p className="text-[13px] text-[#14171c]">{deviationPlace}</p>
                          </div>
                        )}
                        {deviationCreatedBy && (
                          <div>
                            <h3 className="mb-1 text-[11px] font-semibold uppercase tracking-[0.045em] text-[#59616d]">
                              {t("drawings.observation.field.created_by")}
                            </h3>
                            <p className="text-[13px] text-[#14171c]">{deviationCreatedBy}</p>
                          </div>
                        )}
                        {deviationCreatedAt && (
                          <div>
                            <h3 className="mb-1 text-[11px] font-semibold uppercase tracking-[0.045em] text-[#59616d]">
                              {t("drawings.observation.field.created_at")}
                            </h3>
                            <p className="text-[13px] text-[#14171c]">{deviationCreatedAt}</p>
                          </div>
                        )}
                        {deviation?.status != null && (
                          <div>
                            <h3 className="mb-1 text-[11px] font-semibold uppercase tracking-[0.045em] text-[#59616d]">
                              {t("drawings.observation.field.status")}
                            </h3>
                            <p className="text-[13px] text-[#14171c]">
                              {deviationStatusLabel(deviation.status, t)}
                            </p>
                          </div>
                        )}
                      </div>

                      {snapshotDocument && imageUrl(snapshotDocument) && (
                        <div className="overflow-hidden rounded-[10px] border border-[#e5e8ec] bg-white">
                          <h3 className="border-b border-[#e5e8ec] bg-[#f8fafc] px-2.5 py-1.5 text-[10px] font-semibold uppercase tracking-[0.045em] text-[#59616d]">
                            {t("drawings.observation.snapshot_title")}
                          </h3>
                          <a
                            href={fullImageUrl(snapshotDocument)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="block bg-[#f6f7f9]"
                          >
                            <img
                              src={imageUrl(snapshotDocument)}
                              alt={snapshotDocument.title || title}
                              className="max-h-[240px] w-full object-contain"
                            />
                          </a>
                        </div>
                      )}

                      {openDeviationId && (
                        <div className="rounded-[12px] border border-[#e5e8ec] bg-[#f8fafc] p-2.5">
                          <div className="mb-2 flex items-center justify-between gap-2">
                            <p className="text-[11px] font-semibold uppercase tracking-[0.04em] text-[#59616d]">
                              {t("drawings.observation.deviation_docs")}
                            </p>
                            <DirectImageUploadButton
                              module="deviation"
                              moduleParam={openDeviationId}
                              previewOptions={deviationUploadPreviewOptions}
                              className="!min-h-[2.25rem]"
                              onUploadComplete={(docs) => {
                                if (!Array.isArray(docs) || !docs.length) return;
                                setDeviationDocuments((current) => {
                                  const merged = [...(current || [])];
                                  docs.forEach((doc) => {
                                    if (doc?.id == null) return;
                                    if (
                                      !merged.some(
                                        (existing) =>
                                          existing?.id != null &&
                                          String(existing.id) === String(doc.id)
                                      )
                                    ) {
                                      merged.unshift(doc);
                                    }
                                  });
                                  return merged;
                                });
                                // Do NOT hard-close the dialog — soft-close/resume
                                // is handled by document placement session + viewer bridge.
                              }}
                            />
                          </div>
                          <DrawingDocumentPinGroups
                            t={t}
                            documents={deviationDocuments}
                            focusedDocumentId={focusedDocumentId}
                            onFocusDocumentPin={onFocusDocumentPin}
                          />
                        </div>
                      )}

                      <div className="flex flex-wrap gap-2">
                        {openDeviationId && !deviationWorkOrder?.id && (
                          <button
                            type="button"
                            onClick={() => setPerformWorkOpen(true)}
                            className="inline-flex items-center gap-1.5 rounded-[9px] px-3 py-2 text-[13px] font-semibold text-white"
                            style={{
                              background: "linear-gradient(135deg,#1463C7,#2D8BE8)",
                            }}
                          >
                            <WrenchScrewdriverIcon className="h-4 w-4" />
                            {t("deviation.perform_work.submit")}
                          </button>
                        )}
                        {deviationWorkOrder?.id && (
                          <Link
                            to={`/dashboard/work_orders/${deviationWorkOrder.id}`}
                            className="inline-flex items-center gap-1.5 rounded-[9px] border border-[#bfdcf7] bg-[#eaf3fd] px-3 py-2 text-[13px] font-semibold text-[#0b3a78] hover:bg-[#dbeafe]"
                          >
                            <WrenchScrewdriverIcon className="h-4 w-4" />
                            {t("deviation.work_order.open_linked")}
                          </Link>
                        )}
                        {openDeviationId && (
                          <Link
                            to={`/dashboard/hse-ks/deviations/${openDeviationId}`}
                            className="inline-flex items-center gap-1.5 rounded-[9px] border border-[#e5e8ec] bg-white px-3 py-2 text-[13px] font-semibold text-[#2b6ef0] hover:bg-[#eaf1fe]"
                          >
                            {t("drawings.observation.open_deviation_page")}
                            <ArrowTopRightOnSquareIcon className="h-4 w-4" />
                          </Link>
                        )}
                      </div>
                      </>
                      )}
                    </>
                  )}
                </div>
              )}

              {drawing && (
                <div className="mt-4 rounded-[10px] border border-[#e5e8ec] bg-[#f6f7f9] px-3 py-2 text-[12px] text-[#59616d]">
                  <span className="font-semibold text-[#14171c]">{drawing.title}</span>
                  {pageLabel ? ` · ${pageLabel}` : ""}
                  {module.x_position != null && module.y_position != null && (
                    <span className="ml-2 font-mono">
                      x{Math.round(module.x_position)} y{Math.round(module.y_position)}
                    </span>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </PMDialog>
      <DeviationPerformWorkDialog
        open={performWorkOpen}
        onClose={() => setPerformWorkOpen(false)}
        deviation={deviation}
        onSuccess={(result) => {
          const next = result?.deviation;
          if (next?.id) setFetchedDeviation(next);
        }}
      />
    </>
  );
};
